import { useForm } from 'react-hook-form';
import { IFiltersData } from '../models/filtersData';
import { useCookies } from 'react-cookie';
import { useEffect, useMemo, useState } from 'react';
import { IFiltersOptions } from '../models/filtersOptions';
import { DropdownOption } from '../../../../models/common/DropdownOption';
import { IHttpService, HttpService } from '../../../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../../../services/notifierService';
import { UserAccessRightsFiltersCookiesData } from '../data/cookiesData';

export const useUserAccessRightsFilters = () => {
	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const [isFiltersReady, setIsFiltersReady] = useState<boolean>(false);

	const [cookies, setCookie, removeCookie] = useCookies(UserAccessRightsFiltersCookiesData);

	const { control, setValue, reset } = useForm<IFiltersData>({
		defaultValues: {
			userName: '',
			firstName: '',
			lastName: '',
			divisionId: undefined,
			countryIds: [],
			brandIds: [],
			sortBy: '',
			order: ''
		}
	});

	const [filtersOptions, setFiltersOptions] = useState<IFiltersOptions>({
		divisions: [],
		countries: [],
		brands: []
	});

    function populateFilters(divisionOptions: DropdownOption[], countryOptions: DropdownOption[], brandOptions: DropdownOption[]){
        setFiltersOptions({
            divisions: divisionOptions,
            countries: countryOptions,
            brands: brandOptions
        });

        populateDivisionSelectedOptions(divisionOptions);
        populateCountrySelectedOptions(countryOptions);
        populateBrandSelectedOptions(brandOptions);
    }

	function populateDivisionSelectedOptions(divisionOptions: DropdownOption[]) {
		const divisionIdFromCookies = cookies.usersAccessRightsDivision;

		if (divisionOptions.map((d) => d.value).includes(divisionIdFromCookies)) {
			setValue('divisionId', divisionIdFromCookies);
		}
	}

	function populateCountrySelectedOptions(countryOptions: DropdownOption[]) {
		const countriesFromCookies = cookies.usersAccessRightsCountries;

		if (!Array.isArray(countriesFromCookies)) {
			return;
		}

		const countriesOptionsIds = countryOptions.map((x) => x.value);

		const validIds = countriesFromCookies.filter((id: number) =>
			countriesOptionsIds.includes(id)
		);

		if (validIds) {
			setValue('countryIds', validIds);
		}
	}

	function populateBrandSelectedOptions(brandOptions: DropdownOption[]) {
		const brandsFromCookies = cookies.usersAccessRightsBrands;

		if (!Array.isArray(brandsFromCookies)) {
			return;
		}

		const brandsOptionsIds = brandOptions.map((x) => x.value);

		const validIds = brandsFromCookies.filter((id: number) =>
			brandsOptionsIds.includes(id)
		);

		if (validIds) {
			setValue('brandIds', validIds);
		}
	}

	const handleFetchFiltersOptions = async () => {
		const divisions = httpService.get<DropdownOption[]>(
			`divisions/dropdown`,
			undefined
		);

		const countries = httpService.get<DropdownOption[]>(
			`countries/dropdown?searchAll=true`,
			undefined
		);

		const brands = httpService.get<DropdownOption[]>(
			`brands/dropdown`,
			undefined
		);

		try {
			const [divisionOptions, countryOptions, brandOptions] =
				await Promise.all([divisions, countries, brands]);

            populateFilters(divisionOptions, countryOptions, brandOptions);
		} catch (error) {
            notifierService.error();
        } finally{
            setIsFiltersReady(true);
        }
	};

	const clearFilters = () => {
		reset();
		removeCookie("usersAccessRightsDivision");
		removeCookie("usersAccessRightsCountries");
		removeCookie("usersAccessRightsBrands");
	};

	useEffect(() => {
		handleFetchFiltersOptions();
	}, []);

	return {
		control,
		cookies,
		isFiltersReady,
		filtersOptions,
		setValue,
		setCookie,
		clearFilters,
	};
};
